import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft,
  Download,
  Calendar,
  FileText,
  BarChart3,
  PieChart,
  TrendingUp,
  Users,
  Building,
  Target,
  CheckCircle,
  Clock,
  Filter
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GenerateReportPage = () => {
  const navigate = useNavigate();
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState('6m');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  const availableMetrics = [
    { id: 'child-nutrition-coverage', name: 'Child Nutrition Coverage', icon: Target, category: 'Child Development' },
    { id: 'anganwadi-digitization', name: 'Anganwadi Digitization', icon: Building, category: 'Child Development' },
    { id: 'immunization-rate', name: 'Immunization Rate', icon: Users, category: 'Child Development' },
    { id: 'pre-school-enrollment', name: 'Pre-School Enrollment', icon: Building, category: 'Child Development' },
    { id: 'vahli-dikri-beneficiaries', name: 'Vahli Dikri Beneficiaries', icon: TrendingUp, category: 'Women Empowerment' },
    { id: 'widow-pension-coverage', name: 'Widow Pension Coverage', icon: Users, category: 'Women Empowerment' },
    { id: 'violence-prevention-cases', name: 'Violence Prevention Cases', icon: BarChart3, category: 'Women Empowerment' },
    { id: 'women-skill-training', name: 'Women Skill Training', icon: Users, category: 'Women Empowerment' }
  ];

  const dateRanges = [
    { value: '1m', label: 'Last Month' },
    { value: '3m', label: 'Last 3 Months' },
    { value: '6m', label: 'Last 6 Months' },
    { value: '1y', label: 'Last Year' },
    { value: '2y', label: 'Last 2 Years' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const formats = [
    { value: 'pdf', label: 'PDF Report', icon: FileText },
    { value: 'excel', label: 'Excel Spreadsheet', icon: BarChart3 },
    { value: 'ppt', label: 'PowerPoint Presentation', icon: PieChart },
    { value: 'json', label: 'JSON Data Export', icon: FileText }
  ];

  const handleMetricToggle = (metricId) => {
    setSelectedMetrics(prev => 
      prev.includes(metricId) 
        ? prev.filter(id => id !== metricId)
        : [...prev, metricId]
    );
  };

  const handleGenerateReport = async () => {
    if (selectedMetrics.length === 0) {
      alert('Please select at least one metric to include in the report.');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    // Simulate report generation progress
    const progressInterval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setIsGenerating(false);
          // Simulate download
          const filename = `gujarat-wcd-report-${Date.now()}.${selectedFormat}`;
          console.log(`Downloading: ${filename}`);
          alert(`Report generated successfully! File: ${filename}`);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          
          <div className="flex items-center mb-4">
            <div className="text-6xl mr-4">
              <FileText className="w-16 h-16 text-orange-500" />
            </div>
            <div>
              <h1 className="text-4xl font-bold mb-2">Generate Custom Report</h1>
              <p className="text-xl text-blue-100">Create comprehensive analytics reports with selected metrics and timeframes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Report Configuration */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Metric Selection */}
            <div className="lg:col-span-2 space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Filter className="w-5 h-5 mr-2 text-orange-500" />
                    Select Metrics to Include
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {availableMetrics.map(metric => {
                      const Icon = metric.icon;
                      const isSelected = selectedMetrics.includes(metric.id);
                      
                      return (
                        <div
                          key={metric.id}
                          onClick={() => handleMetricToggle(metric.id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                            isSelected 
                              ? 'border-orange-500 bg-orange-50' 
                              : 'border-gray-200 hover:border-orange-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <Icon className={`w-5 h-5 mr-3 ${isSelected ? 'text-orange-500' : 'text-gray-500'}`} />
                              <div>
                                <h4 className="font-medium text-gray-900">{metric.name}</h4>
                                <Badge variant="secondary" className="mt-1 text-xs">
                                  {metric.category}
                                </Badge>
                              </div>
                            </div>
                            {isSelected && (
                              <CheckCircle className="w-5 h-5 text-orange-500" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Date Range Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-orange-500" />
                    Select Time Period
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {dateRanges.map(range => (
                      <button
                        key={range.value}
                        onClick={() => setSelectedDateRange(range.value)}
                        className={`p-3 rounded-lg border transition-all duration-300 ${
                          selectedDateRange === range.value
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Format Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Download className="w-5 h-5 mr-2 text-orange-500" />
                    Export Format
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formats.map(format => {
                      const Icon = format.icon;
                      return (
                        <button
                          key={format.value}
                          onClick={() => setSelectedFormat(format.value)}
                          className={`p-4 rounded-lg border transition-all duration-300 flex items-center ${
                            selectedFormat === format.value
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-gray-200 hover:border-orange-300'
                          }`}
                        >
                          <Icon className="w-5 h-5 mr-3" />
                          {format.label}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Report Preview & Generation */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Report Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Selected Metrics</p>
                    <p className="font-semibold">{selectedMetrics.length} metrics</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Time Period</p>
                    <p className="font-semibold">
                      {dateRanges.find(r => r.value === selectedDateRange)?.label}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Export Format</p>
                    <p className="font-semibold">
                      {formats.find(f => f.value === selectedFormat)?.label}
                    </p>
                  </div>
                  
                  {isGenerating && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Generation Progress</p>
                        <p className="text-sm font-semibold">{Math.round(generationProgress)}%</p>
                      </div>
                      <Progress value={generationProgress} className="h-2" />
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleGenerateReport}
                    disabled={isGenerating || selectedMetrics.length === 0}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                  >
                    {isGenerating ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Report Features</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Executive Summary
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Trend Analysis Charts
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      District-wise Breakdown
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Key Insights & Recommendations
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Interactive Data Tables
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GenerateReportPage;